import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Hotel,
  Car,
  MapPin,
  CalendarRange,
  Repeat2,
  Star,
  Package as PackageIcon,
  Check,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

const formatPrice = (min, max, currency = 'GBP') => {
  if (min == null && max == null) return null;
  const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
  const fmt = (n) => `${symbol}${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (min != null && max != null && Number(min) === Number(max)) return fmt(min);
  if (min != null && max != null) return `${fmt(min)} - ${fmt(max)}`;
  return fmt(min ?? max);
};

const Packages = () => {
  const { currentLanguage, t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  useSeo('packages', currentLanguage);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/packages`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        setPackages(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error('Failed to load packages:', err);
        if (!cancelled) setPackages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  return (
    <div className="pt-12 pb-16 container-custom px-4 space-y-10">
        <header className="glass-card p-8">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-700 bg-sky-50 px-3 py-1 rounded-full mb-4">
            <PackageIcon size={14} />
            {t('packages.eyebrow', 'All-in Treatment Packages')}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3 text-[#0f172a]">
            {t('packages.title', 'Treatment Packages')}
          </h1>
          <p className="text-[#475569] text-lg max-w-3xl">
            {t(
              'packages.subtitle',
              'Curated medical-tourism packages bundling premium dental treatment, 4★/5★ accommodation, VIP transfers and an Alanya sightseeing tour — at a fraction of UK prices.'
            )}
          </p>
        </header>

        {loading ? (
          <div className="glass-card p-8 text-center text-[#64748b]">Loading…</div>
        ) : packages.length === 0 ? (
          <div className="glass-card p-8 text-center text-[#64748b]">
            {t('packages.empty', 'No packages available yet. Please check back soon.')}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {packages.map((p) => {
              const trPrice = formatPrice(p.price_tr_min, p.price_tr_max, p.price_currency);
              const ukPrice = formatPrice(p.price_uk_min, p.price_uk_max, p.price_currency);
              return (
                <article key={p.id} className="glass-card overflow-hidden p-0 flex flex-col">
                  <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-sky-100 to-sky-200">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.title || p.slug}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center">
                        <PackageIcon className="text-sky-500" size={42} />
                      </div>
                    )}
                    {p.is_featured ? (
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold shadow">
                        <Star size={12} /> {t('packages.featured', 'Featured')}
                      </span>
                    ) : null}
                    {p.savings_percent ? (
                      <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold shadow">
                        -{p.savings_percent}% {t('packages.vs_uk', 'vs UK')}
                      </span>
                    ) : null}
                  </div>

                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <h2 className="text-xl md:text-2xl font-bold text-[#0f172a] mb-1">
                      {p.title || p.slug}
                    </h2>
                    {p.subtitle ? (
                      <p className="text-sky-700 text-sm font-semibold mb-3">{p.subtitle}</p>
                    ) : null}
                    {p.short_description ? (
                      <p className="text-[#475569] mb-4 leading-7">{p.short_description}</p>
                    ) : null}

                    <ul className="flex flex-wrap gap-2 text-xs mb-4">
                      {(p.duration_days || p.duration_nights) ? (
                        <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                          <CalendarRange size={12} />
                          {p.duration_days ? `${p.duration_days}d` : ''}
                          {p.duration_nights ? ` / ${p.duration_nights}n` : ''}
                        </li>
                      ) : null}
                      {p.includes_hotel ? (
                        <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">
                          <Hotel size={12} />
                          {p.hotel_stars ? `${p.hotel_stars}★ ` : ''}
                          {t('packages.hotel', 'Hotel')}
                          {p.hotel_nights ? ` · ${p.hotel_nights}n` : ''}
                        </li>
                      ) : null}
                      {p.includes_transfer ? (
                        <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">
                          <Car size={12} /> {t('packages.transfer', 'Transfer')}
                        </li>
                      ) : null}
                      {p.includes_sightseeing ? (
                        <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">
                          <MapPin size={12} /> {t('packages.sightseeing', 'Tour')}
                        </li>
                      ) : null}
                      {p.two_visits ? (
                        <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                          <Repeat2 size={12} /> {t('packages.two_visits', '2 visits required')}
                        </li>
                      ) : null}
                    </ul>

                    {(trPrice || ukPrice) && (
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 mb-4">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          {ukPrice ? (
                            <span className="text-slate-400 line-through text-sm">
                              {t('packages.uk_label', 'UK')} {ukPrice}
                            </span>
                          ) : null}
                          {trPrice ? (
                            <span className="text-2xl font-extrabold text-emerald-700">
                              {trPrice}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-emerald-700 mt-1">
                          {t('packages.from_label', 'All-in package price · per patient')}
                        </div>
                      </div>
                    )}

                    <div className="mt-auto flex items-center justify-between">
                      <Link
                        to={`/packages/${p.slug}`}
                        className="inline-flex items-center gap-2 text-sky-700 font-semibold hover:text-sky-800"
                      >
                        {t('common.learn_more', 'Learn more')}
                        <ArrowRight size={16} />
                      </Link>
                      <Link
                        to="/appointment"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-sky-600 to-sky-500 text-white text-sm font-semibold shadow hover:shadow-lg transition-shadow"
                      >
                        <Check size={14} />
                        {t('packages.cta', 'Get a Quote')}
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default Packages;
